import * as angular from 'angular';
import * as moment from 'moment';

import { IDateRangePickerOptions, ComponentDefinition, IDatePickerDates, IApplicationConfiguration, TimeUnit } from '../../models/index';
import { ICurrentApplicationService } from '../../services';
import { IDateRangeOptionsBuilder, IDateRangePickerService, IMediaHelperService, toDuration } from '../../services/helpers';

class DefaultRangePickerController implements angular.IComponentController, angular.IOnDestroy {
    formController: angular.IFormController;
    isLoaded: boolean;
    isMobile: boolean = false;
    model: IDatePickerDates | moment.Moment;
    onApply: () => void;
    options: IDateRangePickerOptions;
    single: boolean;

    readonly format: string;
    private _config: IApplicationConfiguration;
    private _watchers: (() => void)[];

    static $inject = [
        '$rootScope',
        '$timeout',
        'currentApplicationService',
        'dateRangeOptionsBuilder',
        'dateRangePickerService',
        'mediaHelperService'
    ];
    constructor(
        private _$rootScope: angular.IRootScopeService,
        private _$timeout: angular.ITimeoutService,
        private _currentApplicationService: ICurrentApplicationService,
        private _dateRangeOptionsBuilder: IDateRangeOptionsBuilder,
        private _dateRangePickerService: IDateRangePickerService,
        private _mediaHelper: IMediaHelperService
    ) {
    }

    $onInit() {
        this._config = this._currentApplicationService.getApplicationConfiguration();
        this.initOptions();
        this._watchers = [this._$rootScope.$on(
            '$translateChangeEnd',
            () => this.initOptions()
        )];
    }

    $onDestroy(): void {
        if (this._watchers && this._watchers.length) {
            this._watchers.forEach(w => w());
        }
    }

    $onChanges(changes: angular.IOnChangesObject): void {
        this.isLoaded = false;
        this._$timeout(() => this.initOptions());
    }

    private initOptions(): angular.IPromise<void> {
        this.isMobile = this._mediaHelper.isLargeMobile();

        return this._dateRangeOptionsBuilder
            .getDefaultOptions(null, this.isMobile, this.single)
            .then(options => {
                options.eventHandlers = {
                    'apply.daterangepicker': () => this.apply(),
                    'outsideClick.daterangepicker': () => this.apply()
                };
                options.autoUpdateInput = true;
                options.locale.format = this.format || options.locale.format;
                this.options = options;
            })
            .finally(() => this.isLoaded = true);
    }

    private apply() {
        const config = this._currentApplicationService.getApplicationConfiguration();
        let range: IDatePickerDates;
        if (this.single) {
            range = {
                startDate: moment(this.model as moment.Moment),
                endDate: moment(this.model as moment.Moment).clone().add(toDuration(config))
            };
        } else {
            this.model = this.cleanupDatePickerDate(this.model as IDatePickerDates);
            range = {
                startDate: moment(this.model.startDate),
                endDate: moment(this.model.endDate)
            };
        }
        const aligned = this._dateRangePickerService.enforceAlign(range, { interval: config.interval, intervalTimeUnit: config.intervalTimeUnit }, config);
        this.model = aligned;
        if (this.onApply) {
            this._$timeout(() => this.onApply());
        }
    }

    // End date from picker is always at 23:59:59
    // this trick is done so that hour is resetted
    // and next will compute next correct alignemet given the freq
    // /!\ it should only be called when applying date from picker click, not internal
    private cleanupDatePickerDate(range: IDatePickerDates): IDatePickerDates {
        if (this.single) {
            return range;
        } else {
            const end = (this.model as IDatePickerDates).endDate.clone();

            if (this._config.intervalTimeUnit >= TimeUnit.Day) {
                end.startOf('d');
            }

            return {
                startDate: range.startDate.clone(),
                endDate: end
            };
        }
    }
}

export const wtDefaultRangePicker: ComponentDefinition = {
    componentId: 'wtDefaultRangePicker',
    controller: DefaultRangePickerController,
    controllerAs: 'vm',
    template: require('./defaultRangePicker.component.html'),
    bindings: {
        format: '<?',
        model: '=',
        name: '@',
        onApply: '&?',
        single: '<',
        title: '@?'
    }
};